import { fetchAllOptions, type OptionPage } from 'shared'
/**
 * API客户端配置
 *
 * 提供统一的HTTP请求接口，自动处理：
 * - 认证token
 * - 错误处理
 * - 请求/响应拦截
 *
 * 后端模式：连接 FastAPI 后端 (http://localhost:8000/api/v1)
 * Vite 开发服务器会通过代理转发 /api/v1 请求到后端
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

interface ApiResponse<T = any> {
  success: boolean
  result?: T
  error?: string
  message?: string
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message)
    this.name = 'ApiError'
  }
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private getAuthHeaders(): HeadersInit {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const jsonData = await response.json().catch(() => ({ success: false, error: '响应解析失败' }))

    if (!response.ok) {
      // 处理401未授权
      if (response.status === 401) {
        if (typeof window !== 'undefined') {
          // 如果不在登录页面，清除 token 并重定向到登录页
          if (!window.location.pathname.startsWith('/login')) {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            const currentPath = window.location.pathname + window.location.search
            window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
          }
          // 如果已经在登录页面，不重定向，只抛出错误
        }
        const unauthorizedMessage = typeof jsonData.error === 'string'
          ? jsonData.error
          : jsonData.error?.message || '未授权，请重新登录'
        throw new Error(unauthorizedMessage)
      }

      // 处理其他错误
      let errorMessage: string
      if (typeof jsonData.error === 'string') {
        errorMessage = jsonData.error
      } else if (jsonData.error?.message) {
        // 如果有详细验证错误，附加到消息中
        const details = jsonData.error?.details?.errors
        if (Array.isArray(details) && details.length > 0) {
          const fieldErrors = details.map((e: any) => `${e.field}: ${e.message}`).join('; ')
          errorMessage = `${jsonData.error.message} (${fieldErrors})`
        } else {
          errorMessage = jsonData.error.message
        }
      } else {
        errorMessage = jsonData.message || '请求失败'
      }
      throw new ApiError(errorMessage, response.status, jsonData.code || jsonData.error?.code)
    }

    // 后端返回格式：{ success: true, result: {...} }
    // 直接返回result部分
    return jsonData.result as T
  }

  /**
   * GET 请求
   *
   * @param endpoint API 路径（可含手动拼接的 querystring）
   * @param params 可选查询参数对象：
   *   - 值为 undefined/null 的键会被跳过
   *   - 数组值用 ?foo=a&foo=b 形式拼接
   *   - 如果 endpoint 已含 ? 的 querystring，会合并 ampersand
   *
   * @example
   *   apiClient.get('/orders', { status: 'PAID', page: 1, limit: 20 })
   *   // → GET /orders?status=PAID&page=1&limit=20
   */
  async get<T = any>(
    endpoint: string,
    params?: Record<string, unknown>,
  ): Promise<T> {
    let url = endpoint
    if (params) {
      const search = new URLSearchParams()
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue
        if (Array.isArray(value)) {
          for (const v of value) {
            if (v !== undefined && v !== null) search.append(key, String(v))
          }
        } else {
          search.append(key, String(value))
        }
      }
      const qs = search.toString()
      if (qs) {
        url = endpoint.includes('?') ? `${endpoint}&${qs}` : `${endpoint}?${qs}`
      }
    }

    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<T>(response)
  }

  /** Complete option list; never expose transport pagination in a selector. */
  getAll<T extends OptionPage = any>(endpoint: string): Promise<T> {
    return fetchAllOptions(endpoint, (url) => this.get<T>(url))
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    })
    return this.handleResponse<T>(response)
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    })
    return this.handleResponse<T>(response)
  }

  async delete<T = any>(endpoint: string, body?: any): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
    return this.handleResponse<T>(response)
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: data ? JSON.stringify(data) : undefined,
    })
    return this.handleResponse<T>(response)
  }
}

export const apiClient = new ApiClient(API_BASE_URL)

// API端点定义
export const API_ENDPOINTS = {
  // 认证
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    refresh: '/auth/refresh',
    me: '/auth/me',
  },

  // 订单
  orders: {
    list: '/orders',
    detail: (id: string) => `/orders/${id}`,
    create: '/orders',
    assign: (id: string) => `/orders/${id}/assign`,
    dispatch: (id: string) => `/orders/${id}/dispatch`,
    batchDispatch: '/orders/batch-dispatch',
    batchAssign: '/orders/batch-assign',
  },

  // 工程师任务 (Python: visits)
  engineer: {
    tasks: '/engineer/visits',
    taskDetail: (id: string) => `/engineer/visits/${id}`,
  },

  // 检测
  inspections: {
    start: '/inspections/start',
    detail: (id: string) => `/inspections/${id}`,
    updateData: (id: string) => `/inspections/${id}/data`,
    addSignature: (id: string) => `/inspections/${id}/signature`,
    addPhotos: (id: string) => `/inspections/${id}/photos`,
    complete: (id: string) => `/inspections/${id}/complete`,
  },

  // 校准标签
  calibration: {
    generateLabels: '/calibration/labels',
    verify: '/calibration/verify',
  },

  // 支付
  payments: {
    generate: '/payments/generate',
    submit: '/payments/submit',
    audit: (id: string) => `/payments/${id}/audit`,
    list: '/payments',
    detail: (id: string) => `/payments/${id}`,
  },

  // 证书
  certificates: {
    generate: '/certificates/generate',
    list: '/certificates',
    detail: (id: string) => `/certificates/${id}`,
  },

  // 发票
  invoices: {
    apply: '/invoices/apply',
    issue: (id: string) => `/invoices/${id}/issue`,
    list: '/invoices',
    detail: (id: string) => `/invoices/${id}`,
  },

  // 邮寄追踪
  mailTracking: {
    confirmReceived: (orderId: string) => `/mail-tracking/${orderId}/confirm-received`,
  },

  // 用户
  users: {
    list: '/users',
    detail: (id: string) => `/users/${id}`,
  },

  // 月结管理
  monthly: {
    list: '/monthly',
    reconciliation: '/monthly/reconciliation',
  },

  // 异常管理
  exceptions: {
    list: '/exceptions',
    report: '/exceptions',
    resolve: (id: string) => `/exceptions/${id}/resolve`,
  },

  // 化身（Impersonation） - Python: /admin/impersonate*
  impersonation: {
    start: '/admin/impersonate',
    exit: '/admin/impersonate/exit',
    history: '/admin/impersonations',
    actions: (sessionId: string) => `/admin/impersonations/${sessionId}/actions`,
  },

  // 文件上传
  upload: '/upload',

  // 工作台统计
  dashboard: {
    stats: '/dashboard/stats',
  },
}

// 类型定义
export interface LoginRequest {
  phone: string
  password: string
}

export interface LoginResponse {
  userId: string
  phone: string
  name: string
  roles: string[]
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface Order {
  workflowStageLabel?: string
  workflowStage?: string
  workflowStageEnteredAt?: string
  workflowReceivable?: string

  id: string
  orderNumber: string
  status: string
  type: string
  totalAmount?: number // 兼容旧字段
  standardAmount?: number // 标准价格（正确字段名）
  createdAt: string
  updatedAt: string
  customer?: {
    id: string
    name: string
    phone: string
    email?: string | null
    company?: {
      id: string
      name: string
    } | null
    orderCount?: number
    totalAmount?: number
  }
  user?: {
    id: string
    name: string
    phone: string
    email?: string | null
    company?: {
      id: string
      name: string
    } | null
    orderCount?: number
    totalAmount?: number
  }
  company?: {
    id: string
    name: string
  }
  devices: any[]
  deviceCount?: number
  deviceTotal?: number
  remainingUnassignedCount?: number
  address?: string | null
}

export interface OrderListResponse {
  data: Order[]
  total: number
  page: number
  limit: number
  totalPages: number
}
